import type { WorkflowStatusCountResponse } from '../types'
import type {
  WorkflowFilterStatus,
  WorkflowFilterStatusWithoutShowAll,
} from '@app/shared/types/sf-data'

import type { Ref, ComputedRef } from 'vue'
import { isValidWorkflowStatusResponseKey } from '../predicates'

import { computed, ref } from 'vue'

import { getWorkflowFiltersCount } from '../api'

export const requestStatus = {
  ERROR: 'error',
  INITIAL: 'initial',
  LOADING: 'loading',
  DONE: 'done',
} as const

export type RequestStatus = typeof requestStatus[keyof typeof requestStatus]

export function useWorkflowStatusCount<
  T extends WorkflowFilterStatusWithoutShowAll
>({
  defaultStatus,
  ledgerId,
  statuses = [],
  workflowId,
}: {
  defaultStatus: WorkflowFilterStatus
  ledgerId: number
  statuses: T[]
  workflowId: number
}): {
  counts: Ref<WorkflowStatusCountResponse>
  fetchFilterCounts: () => Promise<void>
  loading: ComputedRef<boolean>
} {
  const counts = ref(
    statuses.reduce(
      (acc, status) => {
        acc[status] = 0
        return acc
      },
      {
        total: 0,
      } as WorkflowStatusCountResponse
    )
  )
  const status = ref<RequestStatus>(requestStatus.INITIAL)

  const loading = computed(() => status.value === requestStatus.LOADING)

  async function fetchFilterCounts(): Promise<void> {
    if (status.value !== requestStatus.INITIAL) {
      return
    }

    status.value = requestStatus.LOADING

    try {
      const result = await getWorkflowFiltersCount({
        defaultStatus,
        ledgerId,
        requestFilters: statuses,
        workflowId,
      })

      if (result) {
        Object.keys(result).forEach((key) => {
          if (!isValidWorkflowStatusResponseKey(key)) {
            return
          }

          const resultValue = result[key]

          if (resultValue) {
            counts.value[key] = resultValue
          }
        })
      }
    } catch {
      status.value = requestStatus.ERROR
    } finally {
      status.value = requestStatus.DONE
    }
  }

  return {
    counts,
    fetchFilterCounts,
    loading,
  }
}
